import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import { useHashScroll } from "@/hooks/useHashScroll";

export default function TestGuide() {
  useHashScroll();

  const testTypes = [
    {
      title: "ガス腐食試験",
      id: "gas-corrosion",
      link: "/service/test-guide/gas-corrosion#gas-corrosion",
      icon: "ri-flask-line",
      bg: "bg-primary-50 border-primary-100",
      iconBg: "bg-primary-100 text-primary-600",
      textColor: "text-primary-900",
      desc: "電子部品や製品における、材料からのアウトガスや大気中の腐食性ガスに対する耐性を評価する試験です。5種の腐食性ガス（H₂S、SO₂、NO₂、Cl₂、O₃）の単独・混合ガス環境で耐久性を測定します。コネクタ、スイッチ、リレーなどの接点部品を中心に、自動車部品、家電、産業機器まで幅広い分野の評価実績があります。",
      specs: [
        "対応ガス：H₂S、SO₂、NO₂、Cl₂、O₃（単独および混合）",
        "対応規格：ISO、IEC、JIS、EIA、Battelle、ASTM等",
        "試験機：自社開発 KGシリーズ（KG200 / KG600）",
        "試験槽内温度・湿度の精密制御により、再現性の高い試験環境を実現",
      ],
    },
    {
      title: "接触抵抗評価試験",
      id: "contact-resistance",
      link: "/service/test-guide/gas-corrosion#contact-resistance",
      icon: "ri-dashboard-line",
      bg: "bg-accent-50 border-accent-100",
      iconBg: "bg-accent-100 text-accent-600",
      textColor: "text-accent-900",
      desc: "接点材料における接触抵抗と接触力の関係を測定・グラフ化し、部品変更時の特性比較評価を行います。板材・丸棒などほとんどの接点材料に対応可能で、腐食性ガス試験の前後比較や経時変化の追跡評価にも活用いただけます。",
      specs: [
        "測定項目：接触抵抗・接触力の関係（µΩレベルの高精度測定）",
        "対象材料：板材・丸棒等、ほとんどの接点材料に対応",
        "試験機：接触抵抗評価装置 MS7500",
        "成果物：説得力のある評価資料（グラフ・数値データ付き）",
        "試験前後比較や経時変化の追跡評価も可能",
      ],
    },
    {
      title: "その他の受託試験",
      id: "other-tests",
      link: "/service/test-guide/env-test#env-test",
      icon: "ri-customer-service-line",
      bg: "bg-secondary-50 border-secondary-100",
      iconBg: "bg-secondary-100 text-secondary-600",
      textColor: "text-secondary-900",
      desc: "上記以外の特殊な試験条件や評価項目についても、お客様のニーズに合わせて対応いたします。高温放置、耐湿試験、冷熱衝撃、振動試験、塩水噴霧試験など各種環境試験も承っております。外部試験機関との連携も可能ですので、まずはご相談ください。",
      specs: [
        "カスタム試験条件のご相談（ガス種・温度・湿度・時間等の自由設定）",
        "高温放置・耐湿試験・冷熱衝撃・振動試験・塩水噴霧試験",
        "外部試験機関との連携対応",
        "試験結果レポートの作成（PDF・紙媒体）",
        "技術的なご相談・改善提案",
      ],
    },
  ];

  const flowSteps = [
    {
      step: "01",
      title: "お問い合わせ",
      desc: "お電話またはメールフォームにて、試験内容・ご希望の試験条件・試料数・納期などをご相談ください。お急ぎの場合はその旨お伝えいただければ、可能な限り対応いたします。",
      icon: "ri-mail-send-line",
    },
    {
      step: "02",
      title: "試験条件の確認",
      desc: "ご希望の規格・ガス種類・濃度・温度・湿度・試験時間などの条件をすり合わせます。試験の目的や対象製品の使用環境を詳しくお聞きし、最適な評価計画をご提案します。",
      icon: "ri-file-list-3-line",
    },
    {
      step: "03",
      title: "見積・日程確認・書類発行",
      desc: `御見積後、試験機の空き状況を確認・調整後予約に関する書類を発行送付いたします。
試験条件、試料詳細、取り扱いについての注意事項をご確認、記載いただきご返送ください。`,
      icon: "ri-calendar-check-line",
    },
    {
      step: "04",
      title: "試料送付",
      desc: "試験開始日の前々日までにご送付または持ち込みをお願いしております。到着後、試料の数量、外観および状態を確認し試験の準備を進めます。試験開始時にサンプル設置状態の写真を送付しますので確認していただきます。",
      icon: "ri-truck-line",
    },
    {
      step: "05",
      title: "試験実施",
      desc: "定められた条件で試験を実施し、温度・湿度・ガス濃度などの環境データを含む詳細な試験記録を作成します。試験中に異常や特記事項があれば、随時ご報告いたします。",
      icon: "ri-flask-line",
    },
    {
      step: "06",
      title: "結果報告",
      desc: "試験結果をレポートにまとめ、PDFまたは紙媒体でお渡しします。グラフや数値データを含む、わかりやすい報告書を作成いたします。結果に関するご質問や追加分析のご要望にも対応可能です。",
      icon: "ri-article-line",
    },
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <div className="relative py-14 md:py-20 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20industrial%20laboratory%20with%20scientific%20testing%20equipment%2C%20clean%20white%20interior%2C%20professional%20atmosphere%2C%20soft%20lighting%2C%20minimal%20composition&width=1600&height=600&seq=testguide-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20" />
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li>
                  <Link to="/service" className="hover:text-background-100 transition-colors">
                    サービス
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">受託試験のご案内</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">
              受託試験のご案内
            </h1>
            <p className="text-accent-300 text-sm mt-2">Contract Testing Services</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Introduction */}
          <SectionTitle
            title="より良い製品づくりのために"
            subtitle="事前の部品評価が品質向上の鍵です"
            centered
          />
          <div className="max-w-3xl mx-auto mt-6 text-center">
            <p className="text-sm text-foreground-600 leading-relaxed">
              電子部品や製品は、どのような環境においても顧客からの要求を満足させる性能を維持しなければなりません。
              ファクトケイでは、お客様のニーズに合わせた各種環境試験を承っております。
              新しい部品を使用する時、部品メーカーを変更する時など、事前の評価で品質リスクを未然に防ぎましょう。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mt-3">
              自社開発のガス腐食試験機KGシリーズと接触抵抗評価装置MS7500を中心に、
              昭和52年の創業以来培ってきた技術とノウハウで、お客様の製品信頼性向上をトータルサポートいたします。
              まずはお気軽にご相談ください。試験の目的やご予算に応じて、最適な試験プランをご提案します。
            </p>
          </div>

          {/* Test Types */}
          <div className="mt-14">
            <SectionTitle
              title="試験の種類"
              subtitle="主な受託試験メニュー"
              centered
            />
            <div className="flex flex-col gap-8 md:gap-10 mt-8 max-w-3xl mx-auto">
              {testTypes.map((t, i) => (
                <Link
                  key={i}
                  to={t.link}
                  id={t.id}
                  className={`block rounded-xl p-8 md:p-10 border ${t.bg} hover:shadow-lg transition-all duration-300 scroll-mt-24 md:scroll-mt-28 group cursor-pointer`}
                >
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${t.iconBg} mb-5`}>
                    <i className={`${t.icon} text-2xl`} />
                  </div>
                  <h3 className={`text-xl md:text-2xl font-bold ${t.textColor} mb-4`}>{t.title}</h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">{t.desc}</p>
                  <ul className="space-y-3 mb-5">
                    {t.specs.map((spec, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-foreground-600">
                        <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0 text-base" />
                        <span>{spec}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors font-label">
                    <span>料金表を見る</span>
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Testing Flow */}
          <div className="mt-16">
            <SectionTitle
              title="試験の流れ"
              subtitle="お申し込みから結果報告まで"
              centered
            />

            {/* Row 1: Steps 01-03 */}
            <div className="mt-8 flex flex-col md:flex-row items-stretch gap-0">
              {flowSteps.slice(0, 3).map((f, i) => (
                <React.Fragment key={i}>
                  <div className="flex-1 bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-base">
                          {f.step}
                        </div>
                      </div>
                      <div>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-2">
                          <i className={`${f.icon} text-lg`} />
                        </div>
                        <h4 className="text-base font-bold text-foreground-900 mb-1">{f.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                  {i < 2 && (
                    <div className="flex items-center justify-center py-3 md:py-0 md:px-3">
                      <i className="ri-arrow-down-line md:hidden text-primary-400 text-2xl" />
                      <i className="ri-arrow-right-line hidden md:block text-primary-400 text-2xl" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Turn connector (desktop): 03 の右 → 下 → 左 → 04 の左へ、1本の連続した線でつなぐ */}
            <div className="hidden md:block relative h-20">
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 1024 80"
                preserveAspectRatio="none"
                fill="none"
              >
                {/* M: 03 の右 (x=1000) から下へ → 左へ回り込み → 04 の左 (x=44) へ降りる */}
                <path
                  d="M 1000 0 V 30 Q 1000 40 990 40 H 54 Q 44 40 44 50 V 78"
                  stroke="oklch(var(--primary-400))"
                  strokeWidth={2}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              {/* ↓ 04 へ入る矢印（他の矢印と同じ primary-400・text-2xl） */}
              <div className="absolute bottom-0 left-[4.3%] -translate-x-1/2 translate-y-1/2 flex items-center justify-center bg-background-50 leading-none">
                <i className="ri-arrow-down-line text-primary-400 text-2xl" />
              </div>
            </div>
            <div className="flex md:hidden justify-center py-3">
              <i className="ri-arrow-down-line text-primary-400 text-2xl" />
            </div>

            {/* Row 2: Steps 04-06 */}
            <div className="flex flex-col md:flex-row items-stretch gap-0">
              {flowSteps.slice(3, 6).map((f, i) => (
                <React.Fragment key={i + 3}>
                  <div className="flex-1 bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-base">
                          {f.step}
                        </div>
                      </div>
                      <div>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-2">
                          <i className={`${f.icon} text-lg`} />
                        </div>
                        <h4 className="text-base font-bold text-foreground-900 mb-1">{f.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                  {i < 2 && (
                    <div className="flex items-center justify-center py-3 md:py-0 md:px-3">
                      <i className="ri-arrow-down-line md:hidden text-primary-400 text-2xl" />
                      <i className="ri-arrow-right-line hidden md:block text-primary-400 text-2xl" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Pricing Links */}
          <div className="mt-16">
            <SectionTitle
              title="受託試験料金表"
              subtitle="試験カテゴリごとに料金表をご確認いただけます"
              centered
            />

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Gas Corrosion Card */}
              <Link
                to="/service/test-guide/gas-corrosion"
                className="group block rounded-xl border border-primary-200 bg-primary-50/50 hover:bg-primary-100/70 transition-all duration-300 overflow-hidden"
              >
                <div className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className="ri-flask-line text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-foreground-900 mb-2">
                        ガス腐食・接触抵抗評価 料金表
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                        ガス腐食試験（シングル・混合ガス）、大型試験槽、接触抵抗測定などの料金を掲載しています。
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          H₂S・SO₂・NO₂・Cl₂・O₃
                        </span>
                        <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          混合ガス
                        </span>
                        <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          接触抵抗
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
                        料金表を見る
                        <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Environmental Test Card */}
              <Link
                to="/service/test-guide/env-test"
                className="group block rounded-xl border border-accent-200 bg-accent-50/50 hover:bg-accent-100/70 transition-all duration-300 overflow-hidden"
              >
                <div className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                      <i className="ri-temp-hot-line text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-foreground-900 mb-2">
                        その他環境試験 料金表
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                        高温放置、耐湿度、冷熱衝撃、振動、塩水噴霧など各種環境試験の料金を掲載しています。
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                          高温・耐湿
                        </span>
                        <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                          振動試験
                        </span>
                        <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                          塩水噴霧
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 group-hover:text-accent-700 transition-colors">
                        料金表を見る
                        <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Usage Rules */}
          <div className="mt-12 bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
            <h3 className="text-base font-bold text-foreground-900 mb-1">受託試験のご利用にあたって</h3>
            <p className="text-xs text-foreground-500 mb-5">料金算出方法・割引・キャンセルポリシーなど</p>

            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">1</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  受託試験料金は次の様に算出します。<br />
                  <span className="text-foreground-900 font-medium">受託試験料金＝基本料金＋単位（時間・種類）×料金</span><br />
                  基本料金には試験報告書が含まれます。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">2</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  長時間試験実施の場合は、使用時間料金の割引に応じますので、ご相談ください。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">3</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  依頼試験のキャンセルまたは日程変更につきましては、速やかにご連絡くださいますようお願い申し上げます。なお、試験準備期間に入ってからのキャンセル等につきましては、所定のキャンセル料を申し受けます。あらかじめご了承賜りますようお願い申し上げます。<br />
                  現在のキャンセル料について&rarr;15営業日前 10％、10営業日前 30％、5営業日前 50％、2営業日前 100％
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">4</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  観察・測定の為試験サンプルを中間抜き取りされる場合は、中間抜き取りのみの場合2,500円～3,000円。抜き取り及び返却送付の場合は3,500円を申し受けます。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">5</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  指定外の試験につきましては、別途費用となります。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">6</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  技術者の立会が必要な場合、別途立会料が必要な場合がありますので、詳細はお問い合せください。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">7</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  本料金表に記載のないその他の試験や分析依頼も応じますので御相談ください。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">8</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  記載の料金には消費税を含んでおりませんので、別途申し受けいたします。
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">9</span>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  記載の料金は<span className="text-foreground-900 font-medium">2026年5月1日</span>現在の料金です。
                </p>
              </li>
            </ol>
          </div>

          {/* Equipment */}
          <div className="mt-16 bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <i className="ri-tools-line text-xl" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground-900">試験機器ラインナップ</h3>
                <p className="text-xs text-foreground-500">自社開発の高性能試験機で正確なデータを提供</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-background-50 rounded-lg p-5 border border-background-200/60">
                <h4 className="text-sm font-bold text-foreground-900 mb-2">ガス腐食試験機 KGシリーズ</h4>
                <div className="space-y-1.5 text-xs text-foreground-600">
                  <p>
                    <Link to="/products#kg200" className="font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors">
                      KG200
                    </Link>
                    {" / "}
                    <Link to="/products#kg600" className="font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors">
                      KG600
                    </Link>
                  </p>
                  <p>5種の腐食性ガス（H₂S、SO₂、NO₂、Cl₂、O₃）に対応した高性能試験機。各種規格に準拠した試験が可能です。</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg p-5 border border-background-200/60">
                <h4 className="text-sm font-bold text-foreground-900 mb-2">接触抵抗評価装置</h4>
                <div className="space-y-1.5 text-xs text-foreground-600">
                  <p>
                    <Link to="/products#ms7500" className="font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors">
                      MS7500
                    </Link>
                  </p>
                  <p>板材・丸棒などほとんどの接点材料における接触抵抗と接触力の関係をグラフ化して評価します。</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-10 bg-primary-700 rounded-xl p-8 md:p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20technical%20background%20with%20soft%20geometric%20shapes%2C%20clean%20minimal%20design%2C%20subtle%20gradient%20light%20blue%20white%20tones%2C%20modern%20professional&width=1200&height=400&seq=testguide-cta&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3">
                受託試験についてのご相談
              </h3>
              <p className="text-sm text-background-200 max-w-xl mx-auto mb-6">
                試験内容や料金についてのご質問、お見積もり依頼など、お気軽にお問い合わせください。
                専門スタッフが丁寧にご対応いたします。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background-50 text-primary-700 font-semibold text-sm hover:bg-background-100 transition-colors"
                >
                  <i className="ri-mail-line" />
                  お問い合わせフォーム
                </Link>
                <Link
                  to="/service"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-background-300 text-background-100 font-semibold text-sm hover:bg-background-50/10 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  サービスページへ戻る
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}